import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Wallet,
  Key,
  Bot,
  ArrowRight,
  Shield,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  RefreshCw,
  Info,
} from 'lucide-react';

const FundingGuide = ({ variant = 'full' }) => {
  const [expanded, setExpanded] = useState(variant === 'full');
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      id: 1,
      title: 'Fund Your Kraken Account',
      icon: Wallet,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-[#94A3B8]">
            First, deposit funds into your Kraken account:
          </p>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Log into your <a href="https://www.kraken.com" target="_blank" rel="noopener noreferrer" className="text-[#2F6BFF] hover:underline">Kraken account</a></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>Go to <strong>"Funding"</strong> and select GBP or USD</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>Deposit via bank transfer (SWIFT/SEPA) or card</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span>Convert your funds to <strong>USD</strong> (the bots trade USD pairs on Kraken)</span>
            </li>
          </ol>
          <div className="bg-[#2F6BFF]/10 border border-[#2F6BFF]/30 rounded-lg p-3 mt-4">
            <p className="text-xs text-[#94A3B8]">
              <Info className="w-4 h-4 inline mr-1 text-[#2F6BFF]" />
              <strong>Why USD?</strong> Our bots trade cryptocurrency pairs like BTC/USD and ETH/USD on Kraken. You can deposit GBP and convert to USD directly on Kraken.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      title: 'Create API Keys on Kraken',
      icon: Key,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-[#94A3B8]">
            Create API keys to allow Apexion to trade on your behalf:
          </p>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Go to Kraken → <strong>Settings</strong> → <strong>API</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>Click <strong>"Create New Key"</strong> and name it "Apexion Trading"</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>Complete 2FA verification</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span>Enable <strong>"Create & Modify Orders"</strong> and <strong>"Query Open Orders & Trades"</strong> permissions</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">5</span>
              <span>Copy the <strong>API Key</strong> and <strong>Private Key</strong></span>
            </li>
          </ol>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mt-4">
            <p className="text-xs text-green-400">
              <Shield className="w-4 h-4 inline mr-1" />
              <strong>Security Tip:</strong> DO NOT enable "Withdraw Funds". Apexion only needs trading permissions - your funds stay secure on Kraken.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 3,
      title: 'Connect Keys to Apexion',
      icon: RefreshCw,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-[#94A3B8]">
            Add your API keys to Apexion:
          </p>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Go to <strong>Dashboard</strong> → <strong>Manage Keys</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>Click <strong>"Add New Key"</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>Paste your API Key and Private Key</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span>Click <strong>"Add & Verify"</strong> - we'll test the connection</span>
            </li>
          </ol>
          <div className="bg-[#0B0D12] border border-[#1E293B] rounded-lg p-3 mt-4">
            <p className="text-xs text-[#94A3B8]">
              <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-400" />
              Your keys are encrypted and stored securely. We never have access to withdraw your funds.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 4,
      title: 'Switch to Live Trading',
      icon: Bot,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-[#94A3B8]">
            When you're ready, switch your bots to live mode:
          </p>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Click on a bot card → <strong>"Settings & Mode"</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>Toggle <strong>"Trading Mode"</strong> from Paper to Live</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>Confirm you understand the risks</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span>The bot will now execute real trades on Kraken!</span>
            </li>
          </ol>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
            <p className="text-xs text-yellow-400">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              <strong>Remember:</strong> Start with a small amount to test. You can always add more capital later.
            </p>
          </div>
        </div>
      ),
    },
  ];

  if (variant === 'compact') {
    return (
      <Card className="bg-[#15171C] border-[#1E293B]">
        <CardHeader 
          className="cursor-pointer pb-3" 
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#2F6BFF]/20">
                <Wallet className="w-5 h-5 text-[#2F6BFF]" />
              </div>
              <div>
                <CardTitle className="text-base font-['Outfit']">How Live Trading Works</CardTitle>
                <p className="text-xs text-[#94A3B8]">Learn how to fund and start live trading</p>
              </div>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-[#94A3B8]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#94A3B8]" />
            )}
          </div>
        </CardHeader>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {steps.map((step, idx) => (
                    <div 
                      key={step.id}
                      className="flex items-center gap-3 p-3 bg-[#0B0D12] rounded-lg"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-[#2F6BFF]' : 'bg-[#1E293B]'
                      }`}>
                        <step.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{step.title}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full mt-4 bg-[#2F6BFF]"
                  onClick={() => window.open('https://www.kraken.com/u/security/api', '_blank')}
                >
                  Create API Keys on Kraken <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    );
  }

  return (
    <Card className="bg-[#15171C] border-[#1E293B]">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2F6BFF]/20">
            <Wallet className="w-6 h-6 text-[#2F6BFF]" />
          </div>
          <div>
            <CardTitle className="font-['Outfit']">Live Trading Setup Guide</CardTitle>
            <p className="text-sm text-[#94A3B8]">Follow these steps to start live trading with Kraken</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {steps.map((step) => (
            <Button
              key={step.id}
              variant={activeStep === step.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveStep(step.id)}
              className={`flex-shrink-0 ${
                activeStep === step.id 
                  ? 'bg-[#2F6BFF]' 
                  : 'border-[#1E293B] text-[#94A3B8]'
              }`}
            >
              <step.icon className="w-4 h-4 mr-2" />
              Step {step.id}
            </Button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="bg-[#0B0D12] rounded-lg p-5 border border-[#1E293B]">
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const StepIcon = steps[activeStep - 1].icon;
                  return (
                    <div className="p-2 rounded-lg bg-[#2F6BFF]/20">
                      <StepIcon className="w-5 h-5 text-[#2F6BFF]" />
                    </div>
                  );
                })()}
                <h3 className="text-lg font-semibold">{steps[activeStep - 1].title}</h3>
              </div>
              {steps[activeStep - 1].content}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            disabled={activeStep === 1}
            className="border-[#1E293B]"
          >
            Previous
          </Button>
          {activeStep < steps.length ? (
            <Button
              onClick={() => setActiveStep(activeStep + 1)}
              className="bg-[#2F6BFF]"
            >
              Next Step <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => window.open('https://www.kraken.com/u/security/api', '_blank')}
              className="bg-green-600 hover:bg-green-700"
            >
              Go to Kraken <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-[#1E293B]">
          <h4 className="text-sm font-semibold mb-3">Key Points to Remember</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-start gap-2 text-xs text-[#94A3B8]">
              <Shield className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-[#F5F7FA]">Your funds stay on Kraken</strong> — we never hold or control your money</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#94A3B8]">
              <DollarSign className="w-4 h-4 text-[#2F6BFF] flex-shrink-0 mt-0.5" />
              <span><strong className="text-[#F5F7FA]">Trade with USD</strong> — deposit GBP and convert to USD on Kraken</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#94A3B8]">
              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-[#F5F7FA]">Never enable withdrawals</strong> — only enable Order trading permissions</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#94A3B8]">
              <RefreshCw className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-[#F5F7FA]">Profits go to your Kraken</strong> — withdraw directly from Kraken anytime</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FundingGuide;
